// Port of SuiteSync/Review.swift's `ReviewNotePlacer` (and FragmentDocument.putComment/placeComment, ProjectDocument's
// reader progress) for web reader links: readers' notes become comments on the live draft, anchored at the checkpoint
// they read — the fragment's heads recorded by the checkpoint, the reader's scalar offsets into the text at those heads,
// and their quote — so each follows its passage forward (or shows as cut). Nothing a reader sends can change the text.
//
// Placing the same note twice does nothing: a note's comment is `review-<note id>`, as the Swift names it (note ids are
// lowercase UUIDs, unique on the relay across reviews). A reader who edits a note after it was placed changes the
// comment's body only. A note whose review, checkpoint or fragment isn't here yet waits (the relay keeps it unplaced);
// one whose range doesn't lie in the text they read is dropped. Progress goes into `settings.readerProgress`, newest per
// review and sender, and a withdrawal removes it.
import * as A from '@automerge/automerge';
import { encodeHeads } from '../automerge/heads.ts';
import { scalarLength } from '../automerge/offsets.ts';
import { ROOT, SwiftReader } from '../automerge/swiftDoc.ts';
import { readFragment, readProject, type CheckpointRecord } from '../model/documents.ts';
import { changeTime, str, timestamp, uint, Writer } from '../edit/typed.ts';
import { checkpointHeadsIn } from '../epochs/reanchor.ts';
import type { ReaderProgress, ReviewNote } from './copy.ts';
import { decodeReaderProgress } from './copy.ts';
import { sortedJSON, toISO8601 } from './json.ts';

export const MAX_BODY = 20_000, MAX_QUOTE = 2_000, MAX_AUTHOR = 200;
const FRAGMENT = 'fragment.';
const DAY_MS = 86_400_000;

/** A Swift `String.prefix(n)` (Characters, approximated by scalars here: bodies are only ever cut past 20,000). */
const prefix = (s: string, n: number) => {
  const chars = [...s];
  return chars.length <= n ? s : chars.slice(0, n).join('');
};

/** One change to one document, with a message and the Swift's whole-second time; returns whether anything changed. */
function change(docs: Map<string, A.Doc<unknown>>, name: string, message: string, at: Date, fn: (w: Writer, r: SwiftReader) => void): boolean {
  const doc = docs.get(name);
  if (!doc) throw new Error(`No document “${name}”`);
  const r = new SwiftReader(doc);
  const before = A.getHeads(doc);
  const next = A.change(doc as A.Doc<Record<string, any>>, { message, time: changeTime(at) }, (d) => fn(new Writer(d, r), r));
  const after = A.getHeads(next);
  if (after.length === before.length && after.every((h, i) => h === before[i])) return false;
  docs.set(name, next as A.Doc<unknown>);
  return true;
}

export interface PlacedComment {
  id: string;
  author: string;
  authorKind: 'human' | 'agent';
  body: string;
  /** Unicode scalars of the fragment's text at `anchorHeads`. */
  anchorStart: number;
  anchorEnd: number;
  anchorHeads: string;
  quote: string;
  createdAt: Date;
}

/**
 * `FragmentDocument.putComment` at given heads (the Swift's `placeComment` anchors at the current heads; a reader's note
 * is anchored where they read it): one change, committed with `message`. Returns the document names changed.
 */
export function placeComment(docs: Map<string, A.Doc<unknown>>, fragmentId: string, c: PlacedComment, message: string, now = new Date()): string[] {
  const name = FRAGMENT + fragmentId;
  const changed = change(docs, name, message, now, (w) => {
    const path = ['comments', c.id];
    w.putIfChanged(path, 'author', str(c.author));
    w.putIfChanged(path, 'authorKind', str(c.authorKind));
    w.putIfChanged(path, 'body', str(c.body));
    w.putIfChanged(path, 'anchorStart', uint(c.anchorStart));
    w.putIfChanged(path, 'anchorEnd', uint(c.anchorEnd));
    w.putIfChanged(path, 'anchorHeads', str(c.anchorHeads));
    w.putIfChanged(path, 'quote', str(c.quote));
    w.putOptional(path, 'parent', null);
    w.putIfChanged(path, 'status', str('open'));
    w.putIfChanged(path, 'createdAt', timestamp(c.createdAt));
    w.putOptional(path, 'image', null);
  });
  return changed ? [name] : [];
}

/** What the placer knows of a review: the checkpoint its copy was made from, and who it was sent to. */
export interface PlacedReview {
  reviewId: string;
  checkpointID: string;
  /** The sender progress is kept under (web links: the round's reader id; else the review id). */
  readerID?: string;
}

export interface IncomingNote {
  reviewId: string;
  note: ReviewNote;
}

export interface IncomingProgress {
  reviewId: string;
  progress: ReaderProgress | null;
  /** The reader withdrew consent: forget what was kept. */
  withdrawn?: boolean;
}

export interface PlaceResult {
  added: number;
  /** Notes edited by their reader after they were placed (the comment's body follows). */
  updated: number;
  /** Notes dealt with (placed, already there, or dropped as unplaceable): the relay can mark them placed. */
  handled: string[];
  /** Notes whose review, checkpoint or fragment isn't on this computer yet. */
  waiting: string[];
  /** Reviews whose progress report (or withdrawal) was dealt with. */
  progressHandled: string[];
  /** Document names changed. */
  changed: string[];
  failures: string[];
}

/** `ProjectDocument.progressKey`. */
export const progressKey = (reviewID: string, reader: string) => `${reviewID}|${reader}`;

/** `settings.readerProgress` as the Swift writes it (sorted keys, ISO 8601 dates). */
export const encodeStoredProgress = (p: ReaderProgress) => sortedJSON(p, toISO8601);

/** Progress kept with the project, newest first (`ProjectDocument.readerProgress`); unreadable values skipped. */
export function storedProgress(project: A.Doc<unknown>): ReaderProgress[] {
  const raw = readProject(project).settings.readerProgress;
  if (!raw || typeof raw !== 'object') return [];
  const out: ReaderProgress[] = [];
  for (const v of Object.values(raw as Record<string, unknown>)) {
    if (typeof v !== 'string') continue;
    try {
      out.push(decodeReaderProgress(v, 'iso8601'));
    } catch {
      // unreadable: left as it is
    }
  }
  return out.sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());
}

const storageKey = (p: ReaderProgress) => progressKey(p.reviewID, p.readerID ?? p.reader);

/**
 * `ReviewNotePlacer.place`: notes and progress for reviews this project knows, written into `docs` (the live documents,
 * replaced by their changed versions). `reviews` maps each review id to its checkpoint.
 */
export function placeReviewNotes(
  docs: Map<string, A.Doc<unknown>>,
  input: { reviews: PlacedReview[]; notes: IncomingNote[]; progress?: IncomingProgress[]; now?: Date },
): PlaceResult {
  const now = input.now ?? new Date();
  const result: PlaceResult = { added: 0, updated: 0, handled: [], waiting: [], progressHandled: [], changed: [], failures: [] };
  const projectDoc = docs.get('project');
  if (!projectDoc) throw new Error('No project document');
  const project = readProject(projectDoc);
  const checkpoints = new Map(project.checkpoints.map((c) => [c.id, c]));
  const reviews = new Map<string, { review: PlacedReview; checkpoint: CheckpointRecord | undefined }>();
  for (const r of input.reviews) reviews.set(r.reviewId, { review: r, checkpoint: checkpoints.get(r.checkpointID) });
  const touched = new Set<string>();

  // Reading progress: kept newest per sender; a withdrawal removes it. The review is the link's, whatever the report
  // claims, and a report must be believable: about a section of the copy they were sent, not from the future.
  for (const incoming of input.progress ?? []) {
    const known = reviews.get(incoming.reviewId);
    if (!known?.checkpoint) continue;
    const sender = known.review.readerID ?? incoming.reviewId;
    try {
      if (incoming.withdrawn || incoming.progress?.withdrawn) {
        const key = progressKey(incoming.reviewId, sender);
        const reader = incoming.progress ? prefix(incoming.progress.reader, MAX_AUTHOR) : 'a reader';
        if (change(docs, 'project', `Reading progress from ${reader}`, now, (w) => w.delete(['settings', 'readerProgress'], key))) touched.add('project');
        result.progressHandled.push(incoming.reviewId);
        continue;
      }
      if (!incoming.progress) continue;
      const p: ReaderProgress = { ...incoming.progress, reviewID: incoming.reviewId, readerID: sender, reader: prefix(incoming.progress.reader, MAX_AUTHOR) };
      result.progressHandled.push(incoming.reviewId);
      if (p.updatedAt.getTime() > now.getTime() + DAY_MS || known.checkpoint.fragmentHeads[p.furthestFragmentID] === undefined) continue;
      const kept = storedProgress(docs.get('project')!);
      const existing = kept.find((x) => storageKey(x) === storageKey(p));
      if (existing && existing.updatedAt.getTime() >= p.updatedAt.getTime()) continue;
      const legacy = kept.find((x) => x.reviewID === incoming.reviewId && x.readerID === undefined && x.reader === p.reader);
      if (change(docs, 'project', `Reading progress from ${p.reader}`, now, (w) => {
        if (legacy) w.delete(['settings', 'readerProgress'], progressKey(incoming.reviewId, p.reader));
        w.putIfChanged(['settings', 'readerProgress'], storageKey(p), str(encodeStoredProgress(p)));
      })) touched.add('project');
    } catch (e) {
      result.failures.push(`Reading progress couldn’t be recorded: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  for (const { reviewId, note } of input.notes) {
    const known = reviews.get(reviewId);
    const hex = known?.checkpoint?.fragmentHeads[note.fragmentID];
    const name = FRAGMENT + note.fragmentID;
    const doc = docs.get(name);
    // Not here yet (the review, its checkpoint or the fragment): try again later.
    if (!known?.checkpoint || hex === undefined || !doc) {
      result.waiting.push(note.id);
      continue;
    }
    try {
      const id = `review-${note.id}`;
      const author = prefix(note.author, MAX_AUTHOR);
      const body = prefix(note.body, MAX_BODY);
      const existing = readFragment(doc).comments.find((c) => c.id === id);
      if (existing) {
        // Edited by its reader since it was placed: the body follows; anything else stays as placed.
        if (existing.body !== body && existing.authorKind === 'human' && existing.author === author) {
          if (change(docs, name, `Comment from ${author}`, now, (w) => w.putIfChanged(['comments', id], 'body', str(body)))) {
            touched.add(name);
            result.updated += 1;
          }
        }
        result.handled.push(note.id);
        continue;
      }
      // A reader's note is untrusted: the range must lie in the text they read, or the comment would fail on every
      // render. Such a note is dropped, not kept. Heads not in this document (not synced yet) wait.
      const heads = checkpointHeadsIn(hex, doc);
      if (!heads) {
        result.waiting.push(note.id);
        continue;
      }
      const text = new SwiftReader(doc, heads).string(ROOT, 'text') ?? '';
      if (!(note.anchorStart <= note.anchorEnd) || note.anchorEnd > scalarLength(text)) {
        result.handled.push(note.id);
        continue;
      }
      const changed = placeComment(docs, note.fragmentID, {
        id, author, authorKind: 'human', body, anchorStart: note.anchorStart, anchorEnd: note.anchorEnd,
        anchorHeads: encodeHeads(heads), quote: prefix(note.quote, MAX_QUOTE), createdAt: note.createdAt,
      }, `Comment from ${author}`, now);
      for (const c of changed) touched.add(c);
      result.added += 1;
      result.handled.push(note.id);
    } catch (e) {
      result.failures.push(`A comment from ${note.author} couldn’t be placed: ${e instanceof Error ? e.message : String(e)}`);
      result.waiting.push(note.id);
    }
  }
  result.changed = [...touched];
  return result;
}
